/* eslint-disable import/order */
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  Button,
  CircularProgress,
  Box,
  TextField,
  MenuItem,
} from '@mui/material';
import {
  Edit,
  Person as PersonIcon,
  ArrowBack,
  ArrowForward,
  Link as LinkIcon,
} from '@mui/icons-material';

import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/axios';

import { useClientes, Cliente } from '../../hooks/useClientes';
import { useCentros } from '../../hooks/useCentros';
import { useAsignaturas } from '../../hooks/useAsignaturas';
import { useProyectos } from '../../hooks/useProyectos';
import { useUsuarios } from '../../hooks/useUsuarios';
import { getTipoAsistentesLabel } from '../../utils/reuniones';
import {
  useCadenaReunion,
  getServicioNombre,
  esReunionApoyoOCounseling,
} from '../../hooks/useReuniones';
import { useReagendarReunion } from '../../hooks/useReuniones';

import ClienteDialog from '../ClienteDialog';
import toast from 'react-hot-toast';
import { useUpdateReunionEstado } from '../../hooks/useReuniones';
import { REUNION_ESTADO_VALUES, getEstadoDisplayLabel } from '../../utils/reuniones';

import moralejaImg from '../../imagenes/Centros/moraleja.png';
import pozueloImg from '../../imagenes/Centros/pozuelo.png';
import plazacastillaImg from '../../imagenes/Centros/plazacastilla.png';
import otherImg from '../../imagenes/Centros/other.png';
import gorilaRelojImg from '../../imagenes/Gorilas/gorila_reloj.png';

/* ───────── Dayjs plugins ───────── */
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
const TZ = 'Europe/Madrid';

/* ───────── tipos auxiliares ───────── */
type Dic = {
  cliente: Record<number, string>;
  centro: Record<number, string>;
  asign: Record<number, string>;
  proy: Record<number, string>;
};

interface Props {
  open: boolean;
  reunion?: any;
  onClose: () => void;
  onEdit?: (row: any) => void;
  onNavigateToReunion?: (reunionId: number) => void;
}

/* ═════════════════════════════════════════════════════════════ */
/*                        ReunionesDialog                        */
/* ═════════════════════════════════════════════════════════════ */
export default function ReunionesDialog({
  open,
  reunion,
  onClose,
  onEdit,
  onNavigateToReunion,
}: Props) {
  const { t, i18n } = useTranslation<'translation'>();
  const { user } = useAuth();

  const isProfesor = user?.roles?.includes('profesor') ?? false;
  const isCliente = user?.roles?.includes('cliente') ?? false;
  const isConsultor = user?.roles?.includes('consultor') ?? false;
  const isStaff = !isProfesor && !isCliente;

  /* ---------- estado local ---------- */
  const [reunionFresh, setReunionFresh] = useState<any | null>(null);
  const [reservaOrigen, setReservaOrigen] = useState<any | null>(null);
  const [selectedEstado, setSelectedEstado] = useState<string | ''>('');
  const updateEstadoMutation = useUpdateReunionEstado();
  const updateEstadoLoading =
    ((updateEstadoMutation as any)?.isLoading as boolean) ||
    updateEstadoMutation.status === 'pending';
  const reagendarMutation = useReagendarReunion();
  const reagendarLoading =
    ((reagendarMutation as any)?.isLoading as boolean) || reagendarMutation.status === 'pending';
  const [motivoDescripcion, setMotivoDescripcion] = useState<string>('');
  const [openReagendado, setOpenReagendado] = useState(false);
  const [reagendadoResult, setReagendadoResult] = useState<{ wc1: any; wc2?: any | null } | null>(null);

  const [clienteSel, setClienteSel] = useState<Cliente | null>(null);
  const [loadingCli, setLoadingCli] = useState(false);

  // Obtener cadena de reuniones si la reunión es parte de una cadena
  const reunionId = reunion?.id || reunionFresh?.id;
  const { data: cadenaReunion, isLoading: loadingCadena } = useCadenaReunion(reunionId);

  /* ---------- hooks de datos ---------- */
  const { rows: clientes = [], update } = useClientes();
  const { rows: centros = [] } = useCentros();
  const { rows: asignaturas = [] } = useAsignaturas();
  const { rows: proyectos = [] } = useProyectos();
  const { rows: usuarios = [] } = useUsuarios({ soloActivos: true });

  const dic = useMemo<Dic>(
    () => ({
      cliente: Object.fromEntries(clientes.map((c) => [c.id, c.nombreCompleto])),
      centro: Object.fromEntries(centros.map((c) => [c.id, c.nombre])),
      asign: Object.fromEntries(asignaturas.map((a) => [a.id, a.nombre])),
      proy: Object.fromEntries(proyectos.map((p) => [p.id, p.nombre])),
    }),
    [clientes, centros, asignaturas, proyectos],
  );

  /* ---------- refrescar detalle de la reunión al abrir ---------- */
  useEffect(() => {
    let cancel = false;
    setReunionFresh(null);
    setReservaOrigen(null);

    if (!open || !reunion?.id) return;

    (async () => {
      try {
        const { data } = await api.get(`/reuniones/${reunion.id}`);
        if (cancel) return;

        const reu = data ?? {};
        setReunionFresh(reu);

        // Si el backend ya incluye la reserva origen como relación, úsala;
        // si no, y hay idReservaOrigen, la cargamos aparte.
        let resOrigen: any =
          reu.reservaOrigen || reu.reserva || reu.reserva_origen || reu.reservaOriginal || null;

        const idReservaOrigen =
          reu.idReservaOrigen ??
          reu.id_reserva_origen ??
          reunion.idReservaOrigen ??
          reunion.id_reserva_origen ??
          null;

        if (!resOrigen && idReservaOrigen) {
          try {
            const { data: reservaData } = await api.get(`/reservas/${idReservaOrigen}`);
            resOrigen = reservaData ?? null;
          } catch {
            // silencioso
          }
        }

        if (!cancel) setReservaOrigen(resOrigen ?? null);
      } catch {
        // silencioso
      }
    })();

    return () => {
      cancel = true;
    };
  }, [open, reunion?.id]);

  /* ---------- combinar prop + refresco ---------- */
  const reunionEff = useMemo(
    () => ({ ...(reunion ?? {}), ...(reunionFresh ?? {}) }),
    [reunion, reunionFresh],
  );

  // Sincronizar estado seleccionado cuando cambie la reunión efectiva
  useEffect(() => {
    const estado = (reunionEff as any)?.estado ?? '';
    setSelectedEstado(estado ?? '');
    // no precargamos comentario en el textarea por defecto (dejar vacío)
    setMotivoDescripcion('');
  }, [reunionEff]);

  /* ---------- campos básicos de reunión ---------- */
  const anyReu = reunionEff as any;

  const idAlumno: number | undefined =
    anyReu?.idAlumno ?? anyReu?.id_alumno ?? anyReu?.alumnoId ?? undefined;

  const idStaff: number | undefined =
    anyReu?.idStaff ?? anyReu?.id_staff ?? anyReu?.staffId ?? undefined;

  // Cliente principal de la reunión (familia/alumno)
  const alumnoNombre: string =
    anyReu?.alumno?.nombreCompleto ??
    anyReu?.alumno?.nombre ??
    anyReu?.alumnoNombre ??
    (idAlumno != null ? dic.cliente[idAlumno] : undefined) ??
    '—';

  // Staff que lleva la reunión: primero intentamos relación, luego buscamos en usuarios por id_staff
  const staffFromList =
    idStaff != null
      ? (usuarios as any[]).find((u) => Number((u as any).id) === Number(idStaff))
      : undefined;

  const staffNombre: string =
    anyReu?.staff?.nombreCompleto ??
    anyReu?.staff?.nombre ??
    anyReu?.staffNombre ??
    (staffFromList as any)?.nombreCompleto ??
    (staffFromList as any)?.nombre ??
    '—';

  const tipoLabel: string = anyReu?.tipo ?? anyReu?.tipoReunion ?? anyReu?.tipo_reunion ?? '';

  const estadoLegible: string =
    anyReu?.estado ??
    anyReu?.estado_label ??
    anyReu?.estadoDescripcion ??
    anyReu?.estadoCode ??
    anyReu?.estado_code ??
    '—';

  const canal: string = anyReu?.canal ?? '';
  const tipoAsistentes: string = anyReu?.tipoAsistentes ?? anyReu?.tipo_asistentes ?? '';

  const comentariosPublicos: string =
    anyReu?.comentariosPublicos ?? anyReu?.comentarios_publicos ?? '';

  const comentariosInternos: string =
    anyReu?.comentariosInternos ?? anyReu?.comentarios_internos ?? '';

  const origen: string = anyReu?.origen ?? '';

  const fechaInicioStr: string | null = anyReu?.fechaInicio ?? anyReu?.fecha_inicio ?? null;
  const fechaFinStr: string | null = anyReu?.fechaFin ?? anyReu?.fecha_fin ?? null;

  const start = fechaInicioStr ? dayjs(fechaInicioStr).tz(TZ) : null;
  const end = fechaFinStr ? dayjs(fechaFinStr).tz(TZ) : null;

  const fechaLabel = start != null ? start.locale(i18n.language).format('dddd D MMMM YYYY') : '—';

  const horaLabel =
    start != null && end != null
      ? `${start.format('HH:mm')} - ${end.format('HH:mm')}`
      : start != null
        ? start.format('HH:mm')
        : '—';

  const fechaEventoRefStr: string | null =
    anyReu?.fechaEventoReferencia ?? anyReu?.fecha_evento_referencia ?? null;
  const tipoEventoRef: string =
    anyReu?.tipoEventoReferencia ?? anyReu?.tipo_evento_referencia ?? '';

  const fechaEventoRefLabel =
    fechaEventoRefStr != null
      ? dayjs(fechaEventoRefStr).tz(TZ).locale(i18n.language).format('DD/MM/YYYY HH:mm')
      : null;

  /* ---------- datos provenientes (si existen) de la reserva origen ---------- */
  const anyRes: any = reservaOrigen ?? anyReu?.reservaOrigen ?? anyReu?.reserva ?? null;

  const centroIdFromReserva: number | undefined =
    anyRes?.idCentro ?? anyRes?.centroId ?? anyRes?.id_centro ?? undefined;
  const centroIdFromReunion: number | undefined =
    anyReu?.idCentro ?? anyReu?.centroId ?? anyReu?.id_centro ?? undefined;

  const centroNombre: string =
    anyRes?.nombreCentro ??
    anyRes?.centro?.nombre ??
    anyReu?.nombreCentro ??
    (centroIdFromReserva && dic.centro[centroIdFromReserva]) ??
    (centroIdFromReunion && dic.centro[centroIdFromReunion]) ??
    '—';

  const proyectoNombre: string =
    anyRes?.nombreProyecto ??
    (anyRes?.idProyecto && dic.proy[anyRes.idProyecto]) ??
    anyReu?.nombreProyecto ??
    (anyReu?.idProyecto && dic.proy[anyReu.idProyecto]) ??
    '';

  const asignaturaNombre: string =
    anyRes?.nombreAsignatura ??
    (anyRes?.idAsignatura && dic.asign[anyRes.idAsignatura]) ??
    anyReu?.nombreAsignatura ??
    (anyReu?.idAsignatura && dic.asign[anyReu.idAsignatura]) ??
    '';

  const studentYear: string | undefined =
    anyRes?.studentYear ?? anyRes?.curso ?? anyReu?.studentYear ?? anyReu?.curso;

  const examBoard: string | undefined =
    anyRes?.examBoard ?? anyRes?.examinationBoard ?? anyReu?.examBoard ?? anyReu?.examinationBoard;

  // Para el botón "Ver ficha cliente"
  const titularUsrId: number | undefined = idAlumno;

  // Información de cadena
  const servicioId = anyReu?.servicioId ?? null;
  const esParteDeCadena = servicioId && esReunionApoyoOCounseling(anyReu);
  const servicioNombre = servicioId ? getServicioNombre(servicioId) : null;
  const posicionEnCadena = cadenaReunion?.posicion ?? null;
  const totalEnCadena = cadenaReunion?.total ?? null;
  const reunionAnterior = cadenaReunion?.anterior ?? null;
  const reunionSiguiente = cadenaReunion?.siguiente ?? null;

  // Handler para navegar a otra reunión
  const handleNavigateToReunion = useCallback(
    (reunionId: number) => {
      if (onNavigateToReunion) {
        onNavigateToReunion(reunionId);
      } else {
        // Fallback: cerrar y abrir nueva reunión
        onClose();
        setTimeout(() => {
          // Disparar evento para que el calendario abra la nueva reunión
          window.dispatchEvent(new CustomEvent('reunion:navigate', { detail: { reunionId } }));
        }, 100);
      }
    },
    [onNavigateToReunion, onClose],
  );

  /* ---------- early return ---------- */
  if (!reunion) return null;

  /* ═════════════════════ render ═════════════════════ */
  return (
    <Dialog
      key={anyReu?.id ?? 'none'}
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(8px)',
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          overflow: 'visible',
          ml: { xs: 0, md: '80px' },
          border: '1px solid #000',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        },
      }}
    >
      {/* Gorila lateral */}
      <Box
        component="img"
        src={gorilaRelojImg}
        alt="Gorila"
        sx={{
          position: 'absolute',
          left: 0,
          bottom: { xs: '0px', sm: '0px', md: '0px' },
          transform: 'translateX(-100%)',
          width: { xs: '250px', sm: '300px', md: '350px' },
          height: 'auto',
          zIndex: 1,
          display: { xs: 'none', sm: 'block' },
        }}
      />

      <DialogTitle
        sx={{
          backgroundColor: '#000',
          color: '#fff',
          fontWeight: 'bold',
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px',
        }}
      >
        {`Reunión #${anyReu?.id ?? ''}`}
      </DialogTitle>

      <DialogContent dividers sx={{ minHeight: 260, backgroundColor: '#fff' }}>
        <Stack spacing={2}>
          {/* ───────── Sección amarilla con información básica ───────── */}
          <Box
            sx={{
              position: 'relative',
              p: 2.5,
              borderRadius: 2,
              backgroundColor: '#fcf1ad',
              display: 'flex',
              gap: 2,
              alignItems: 'flex-start',
            }}
          >
            {/* Icono de edificio */}
            <Box
              sx={{
                width: 60,
                height: 60,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                component="img"
                src={
                  centroIdFromReserva === 1 || centroIdFromReunion === 1
                    ? moralejaImg
                    : centroIdFromReserva === 2 || centroIdFromReunion === 2
                      ? pozueloImg
                      : centroIdFromReserva === 3 || centroIdFromReunion === 3
                        ? plazacastillaImg
                        : otherImg
                }
                alt={centroNombre || 'Centro'}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#000' }}>
                    Reunión
                  </Typography>
                  {tipoLabel && (
                    <Box
                      sx={{
                        bgcolor: '#21a598',
                        color: '#fff',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '999px',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        textTransform: 'lowercase',
                      }}
                    >
                      {tipoLabel}
                    </Box>
                  )}
                </Box>
                <Typography variant="body2" sx={{ color: '#000' }}>
                  <strong>Fecha:</strong> {fechaLabel}
                </Typography>
                <Typography variant="body2" sx={{ color: '#000' }}>
                  <strong>Hora:</strong> {horaLabel}
                </Typography>
                <Typography variant="body2" sx={{ color: '#000' }}>
                  <strong>Centro:</strong> {centroNombre}
                </Typography>

                {/* Controles para staff: selector de estado + botón + motivo */}
                {isStaff ? (
                  <Stack spacing={1} sx={{ width: '100%', alignItems: 'flex-start', mt: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        select
                        size="small"
                        value={selectedEstado}
                        onChange={(e) => setSelectedEstado(e.target.value)}
                        sx={{ minWidth: 220 }}
                        helperText=""
                      >
                        {REUNION_ESTADO_VALUES.map((ev) => (
                          <MenuItem key={ev} value={ev}>
                            {getEstadoDisplayLabel(ev)}
                          </MenuItem>
                        ))}
                      </TextField>

                      <Button
                        variant="contained"
                        size="small"
                        disabled={
                          !selectedEstado ||
                          selectedEstado === (anyReu?.estado ?? '') ||
                          updateEstadoLoading
                        }
                        onClick={async () => {
                          if (!anyReu?.id || !selectedEstado) return;
                          try {
                            await updateEstadoMutation.mutateAsync({
                              id: anyReu.id,
                              estado: selectedEstado as any,
                              descripcion: motivoDescripcion || undefined,
                            });
                            toast.success(t('reunionesDialog.saveEstadoSuccess'));
                            // Refrescar detalle localmente (el hook invalidará cache)
                            try {
                              const { data } = await api.get(`/reuniones/${anyReu.id}`);
                              setReunionFresh(data ?? null);
                            } catch {
                              // silencioso
                            }
                          } catch (err) {
                            toast.error(t('reunionesDialog.saveEstadoError'));
                          }
                        }}
                        sx={{
                          backgroundColor: '#21a598',
                          '&:hover': { backgroundColor: '#197a6e' },
                        }}
                      >
                        {updateEstadoLoading ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          t('reunionesDialog.saveEstado')
                        )}
                      </Button>
                    </Stack>

                    <TextField
                      multiline
                      fullWidth
                      minRows={2}
                      maxRows={6}
                      size="small"
                      label={String(t('reunionesDialog.motivoLabel'))}
                      placeholder={String(t('reunionesDialog.motivoPlaceholder'))}
                      value={motivoDescripcion}
                      onChange={(e) => setMotivoDescripcion(e.target.value)}
                    />
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#555', mt: 1 }}>
                    {estadoLegible}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Box>

          {/* ───────── Información de cadena (si es parte de una) ───────── */}
          {esParteDeCadena && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: '#e3f2fd',
                borderLeft: '4px solid #2196f3',
              }}
            >
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  <LinkIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
                  Parte de seguimiento: {servicioNombre}
                </Typography>
                {posicionEnCadena && totalEnCadena && (
                  <Typography variant="body2" color="text.secondary">
                    Reunión {posicionEnCadena} de {totalEnCadena}
                  </Typography>
                )}
                <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                  {reunionAnterior && (
                    <Button
                      size="small"
                      startIcon={<ArrowBack />}
                      onClick={() => handleNavigateToReunion(reunionAnterior.id)}
                      sx={{
                        textTransform: 'none',
                        color: '#1976d2',
                        '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' },
                      }}
                    >
                      Reunión anterior (#{reunionAnterior.id})
                    </Button>
                  )}
                  {reunionSiguiente && (
                    <Button
                      size="small"
                      endIcon={<ArrowForward />}
                      onClick={() => handleNavigateToReunion(reunionSiguiente.id)}
                      sx={{
                        textTransform: 'none',
                        color: '#1976d2',
                        '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' },
                      }}
                    >
                      Reunión siguiente (#{reunionSiguiente.id})
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Box>
          )}

          {/* ───────── Sección Información ───────── */}
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 1.5,
                pb: 1,
                borderBottom: '1px solid #e0e0e0',
              }}
            >
              Información
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2">
                <strong>Staff:</strong> {staffNombre || '—'}
              </Typography>

              <Typography variant="body2">
                <strong>Familia/Alumno(s):</strong> {alumnoNombre}
              </Typography>

              {canal && (
                <Typography variant="body2">
                  <strong>Canal:</strong> {canal}
                </Typography>
              )}

              {tipoAsistentes && (
                <Typography variant="body2">
                  <strong>Tipo de asistentes:</strong>{' '}
                  {getTipoAsistentesLabel(tipoAsistentes as any)}
                </Typography>
              )}

              {fechaEventoRefLabel && (
                <Typography variant="body2">
                  <strong>Fecha evento referencia:</strong> {fechaEventoRefLabel}
                  {tipoEventoRef ? ` (${tipoEventoRef})` : ''}
                </Typography>
              )}

              {origen && (
                <Typography variant="body2">
                  <strong>Origen:</strong> {origen}
                </Typography>
              )}

              {comentariosPublicos && (
                <Typography variant="body2">
                  <strong>Comentarios (familia):</strong> {comentariosPublicos}
                </Typography>
              )}

              {isStaff && comentariosInternos && (
                <Typography variant="body2">
                  <strong>Comentarios internos:</strong> {comentariosInternos}
                </Typography>
              )}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          paddingRight: '25px',
          paddingBottom: '15px',
          justifyContent: 'flex-end',
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: '#21a598',
            borderColor: '#21a598',
            textTransform: 'uppercase',
            '&:hover': {
              borderColor: '#21a598',
              backgroundColor: 'rgba(33,165,152,0.08)',
            },
          }}
        >
          Cerrar
        </Button>

        {isStaff && titularUsrId && (
          <Button
            variant="outlined"
            startIcon={loadingCli ? <CircularProgress size={18} /> : <PersonIcon />}
            sx={{
              color: '#21a598',
              borderColor: '#21a598',
              '&:hover': {
                borderColor: '#21a598',
                backgroundColor: 'rgba(33,165,152,0.08)',
              },
            }}
            onClick={async () => {
              const titularCache = clientes.find((c) => c.id === titularUsrId);
              if (titularCache) {
                setClienteSel(titularCache);
                return;
              }
              try {
                setLoadingCli(true);
                const { data } = await api.get<Cliente>(`/usuarios/${titularUsrId}`);
                setClienteSel(data);
              } catch (e) {
                // No se pudo cargar el cliente
              } finally {
                setLoadingCli(false);
              }
            }}
          >
            Ver ficha cliente
          </Button>
        )}

        {isStaff && !isConsultor && onEdit && (
          <Button
            variant="contained"
            startIcon={<Edit />}
            sx={{
              backgroundColor: '#21a598',
              '&:hover': { backgroundColor: '#1a8578' },
              boxShadow: 'none',
            }}
            onClick={async () => {
              try {
                const { data } = await api.get(`/reuniones/${anyReu?.id}`);
                onClose();
                setTimeout(() => {
                  onEdit({ ...reunionEff, ...data });
                }, 0);
              } catch (e) {
                // No se pudo cargar la reunión
              }
            }}
          >
            Editar
          </Button>
        )}
        {isStaff && !isConsultor && (
          <Button
            variant="outlined"
            sx={{
              color: '#d16000',
              borderColor: '#d16000',
              textTransform: 'none',
              mr: 1,
            }}
            startIcon={reagendarLoading ? <CircularProgress size={18} /> : undefined}
            onClick={async () => {
              if (!anyReu?.id) return;
              try {
                const result = await reagendarMutation.mutateAsync(anyReu.id);
                // Mostrar diálogo de éxito con detalles de las reuniones reagendadas
                setReagendadoResult(result ?? null);
                setOpenReagendado(true);
                toast.success(t('reunionesDialog.reagendarSuccess', 'Reagendado correctamente'));
                // Refrescar detalle
                try {
                  const { data } = await api.get(`/reuniones/${anyReu.id}`);
                  setReunionFresh(data ?? null);
                } catch {
                  // silencioso
                }
              } catch (err: any) {
                const msg =
                  err?.response?.data?.message ||
                  t('reunionesDialog.reagendarError', 'No se pudo reagendar');
                toast.error(msg);
              }
            }}
          >
            Reagendar
          </Button>
        )}
      </DialogActions>

      {clienteSel && (
        <ClienteDialog
          open
          initial={clienteSel}
          readOnly={isConsultor}
          onClose={() => setClienteSel(null)}
          onSave={
            isConsultor
              ? undefined
              : async (d) => {
                  await update(clienteSel.id, d);
                  setClienteSel(null);
                }
          }
        />
      )}
      {/* Dialogo de confirmación de reagendado */}
      <Dialog open={openReagendado} onClose={() => setOpenReagendado(false)}>
        <DialogTitle>{t('reunionesDialog.reagendadoDialog.title', 'Reagendado')}</DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            <Typography>
              {t('reunionesDialog.reagendadoDialog.message', 'La reunión se ha reagendado correctamente.')}
            </Typography>
            {reagendadoResult?.wc1 && (
              <Typography variant="body2">
                {t('reunionesDialog.reagendadoDialog.wc1', 'WC1')}: {dayjs(reagendadoResult.wc1.fechaInicio).tz(TZ).format('YYYY-MM-DD HH:mm')}
              </Typography>
            )}
            {reagendadoResult?.wc2 && (
              <Typography variant="body2">
                {t('reunionesDialog.reagendadoDialog.wc2', 'WC2')}: {dayjs(reagendadoResult.wc2.fechaInicio).tz(TZ).format('YYYY-MM-DD HH:mm')}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReagendado(false)}>{t('close', 'Cerrar')}</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
